import { useState, useEffect, useCallback } from "react";
import { useDemo } from "../context/DemoContext";
import { formatCurrencyAmount } from "../data/mock";
import {
  listCustomers,
  listCustomerInternalAccounts,
  listPlatformInternalAccounts,
  listCustomerExternalAccounts,
  transferIn,
  transferOut,
  createQuote,
  executeQuote,
  getConfig,
} from "../services/gridApi";

type DestinationType = "external-account" | "uma-address" | "same-currency";
type Direction = "transfer-out" | "transfer-in";
type Step = "setup" | "review" | "result";

const FALLBACK_CURRENCIES = ["USD", "EUR", "GBP", "BRL", "MXN", "BTC", "USDC"];

function formatAmount(amount: number, currency: string): string {
  if (currency === "BTC") return (amount / 100_000_000).toFixed(8) + " BTC";
  return (amount / 100).toFixed(2) + " " + currency;
}

/** Safely format an amount object where currency can be string or object */
function safeFormatAmount(amt: any): string {
  if (!amt) return "--";
  if (typeof amt.currency === "object" && amt.currency?.code) {
    return formatCurrencyAmount(amt);
  }
  // API may return currency as a string
  const code = typeof amt.currency === "string" ? amt.currency : "?";
  const decimals = code === "BTC" ? 8 : 2;
  const value = amt.amount / Math.pow(10, decimals);
  return `${value.toFixed(2)} ${code}`;
}

function amountToMinorUnits(amount: number, currency: string): number {
  if (currency === "BTC") return Math.round(amount * 100_000_000);
  return Math.round(amount * 100);
}

function customerDisplayName(c: any): string {
  return c.fullName || c.businessInfo?.legalName || c.platformCustomerId || c.id;
}

export default function Transfer() {
  const {
    demo,
    getDemoInternalAccounts,
    getDemoExternalAccounts,
    getDemoCustomers,
    demoCreateQuote,
    demoExecuteQuote,
    demoTransferOut,
    demoTransferIn,
  } = useDemo();

  // ─── Step state ──────────────────────────────────────────────
  const [step, setStep] = useState<Step>("setup");

  // ─── Data loaded from API or demo ─────────────────────────────
  const [customers, setCustomers] = useState<any[]>([]);
  const [supportedCurrencies, setSupportedCurrencies] = useState<string[]>(FALLBACK_CURRENCIES);
  const [internalAccounts, setInternalAccounts] = useState<any[]>([]);
  const [externalAccounts, setExternalAccounts] = useState<any[]>([]);

  // ─── Form state ──────────────────────────────────────────────
  const [customerId, setCustomerId] = useState("");
  const [sourceCurrency, setSourceCurrency] = useState("");
  const [destType, setDestType] = useState<DestinationType>("external-account");
  const [destExternalAccountId, setDestExternalAccountId] = useState("");
  const [destUmaAddress, setDestUmaAddress] = useState("");
  const [destUmaCurrency, setDestUmaCurrency] = useState("");
  const [direction, setDirection] = useState<Direction>("transfer-out");
  const [sameCurrencyInternalAccountId, setSameCurrencyInternalAccountId] = useState("");
  const [sameCurrencyExternalAccountId, setSameCurrencyExternalAccountId] = useState("");
  const [sourceAccountId, setSourceAccountId] = useState("");
  const [amount, setAmount] = useState("");
  const [lockedSide, setLockedSide] = useState<"SENDING" | "RECEIVING">("SENDING");

  // ─── Transient state ─────────────────────────────────────────
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [quote, setQuote] = useState<any>(null);
  const [result, setResult] = useState<any>(null);

  // ─── Load customers on mount ──────────────────────────────────
  useEffect(() => {
    if (demo.enabled) {
      setCustomers(getDemoCustomers());
      return;
    }
    listCustomers({ limit: 100 })
      .then((r) => setCustomers(r.data || []))
      .catch(() => {});

    getConfig()
      .then((config) => {
        const currencies: string[] =
          config?.supportedCurrencies?.map((c: any) =>
            typeof c === "string" ? c : c.currencyCode
          ) || [];
        if (currencies.length > 0) {
          setSupportedCurrencies(currencies);
        }
      })
      .catch(() => {});
  }, [demo.enabled, getDemoCustomers]);

  // ─── Load accounts when customer changes ─────────────────────
  useEffect(() => {
    if (!customerId) {
      setInternalAccounts([]);
      setExternalAccounts([]);
      setDestExternalAccountId("");
      setSameCurrencyInternalAccountId("");
      setSameCurrencyExternalAccountId("");
      setSourceAccountId("");
      return;
    }

    setDestExternalAccountId("");
    setSameCurrencyInternalAccountId("");
    setSameCurrencyExternalAccountId("");
    setSourceAccountId("");

    if (demo.enabled) {
      setInternalAccounts(getDemoInternalAccounts(customerId));
      setExternalAccounts(getDemoExternalAccounts(customerId));
      return;
    }

    Promise.all([
      listCustomerInternalAccounts({ customerId }).catch(() => ({ data: [] })),
      listPlatformInternalAccounts({}).catch(() => ({ data: [] })),
      listCustomerExternalAccounts({ customerId }).catch(() => ({ data: [] })),
    ]).then(([custIa, platIa, ea]) => {
      const custAccounts = (custIa.data || []).map((a: any) => ({ ...a, _label: "Customer" }));
      const platAccounts = (platIa.data || []).map((a: any) => ({ ...a, _label: "Platform" }));
      const custIds = new Set(custAccounts.map((a: any) => a.id));
      const combined = [...custAccounts, ...platAccounts.filter((a: any) => !custIds.has(a.id))];
      setInternalAccounts(combined);
      setExternalAccounts(ea.data || []);
    });
  }, [customerId, demo.enabled, getDemoInternalAccounts, getDemoExternalAccounts]);

  // ─── Default source currency when currencies load ────────────
  useEffect(() => {
    if (!sourceCurrency && supportedCurrencies.length > 0) {
      setSourceCurrency(supportedCurrencies[0]);
    }
  }, [supportedCurrencies, sourceCurrency]);

  // ─── Default UMA destination currency ────────────────────────
  useEffect(() => {
    if (!destUmaCurrency && supportedCurrencies.length > 0) {
      setDestUmaCurrency(supportedCurrencies[0]);
    }
  }, [supportedCurrencies, destUmaCurrency]);

  // ─── Demo: Create Quote (Internal → External Account or UMA) ──
  const handleDemoCreateQuote = useCallback(() => {
    setLoading(true);
    setError("");

    try {
      const parsed = parseFloat(amount);
      if (isNaN(parsed) || parsed <= 0) throw new Error("Enter a valid positive amount");
      if (!sourceAccountId) throw new Error("Select a source internal account");

      let destCurrency = "";
      let destAccountId: string | undefined;
      let destUma: string | undefined;

      if (destType === "external-account") {
        if (!destExternalAccountId) throw new Error("Select a destination external account");
        const ea = externalAccounts.find((a: any) => a.id === destExternalAccountId);
        destCurrency = ea?.currency?.code || ea?.currency || "USD";
        destAccountId = destExternalAccountId;
      } else if (destType === "uma-address") {
        if (!destUmaAddress.trim()) throw new Error("Enter a UMA address");
        destCurrency = destUmaCurrency || "USD";
        destUma = destUmaAddress.trim();
      }

      const res = demoCreateQuote({
        sourceAccountId,
        destinationAccountId: destAccountId,
        destinationUmaAddress: destUma,
        destinationCurrency: destCurrency,
        amount: parsed,
        lockedSide,
      });

      if (res.success && res.quote) {
        setQuote(res.quote);
        setStep("review");
      } else {
        setError(res.error || "Quote creation failed");
      }
    } catch (e: any) {
      setError(e.message || "Quote creation failed");
    } finally {
      setLoading(false);
    }
  }, [amount, sourceAccountId, destType, destExternalAccountId, destUmaAddress, destUmaCurrency, lockedSide, externalAccounts, demoCreateQuote]);

  // ─── Demo: Execute Quote ──────────────────────────────────────
  const handleDemoExecuteQuote = useCallback(() => {
    if (!quote) return;
    setLoading(true);
    setError("");

    const res = demoExecuteQuote(quote.quoteId);
    if (res.success) {
      setResult(res.transaction);
      setStep("result");
      // Refresh accounts
      if (customerId) setInternalAccounts(getDemoInternalAccounts(customerId));
    } else {
      setError(res.error || "Quote execution failed");
    }
    setLoading(false);
  }, [quote, customerId, demoExecuteQuote, getDemoInternalAccounts]);

  // ─── Demo: Same-Currency Transfer ─────────────────────────────
  const handleDemoSameCurrencyTransfer = useCallback(() => {
    setLoading(true);
    setError("");

    try {
      const parsed = parseFloat(amount);
      if (isNaN(parsed) || parsed <= 0) throw new Error("Enter a valid positive amount");

      let res;
      if (direction === "transfer-out") {
        res = demoTransferOut({
          sourceInternalAccountId: sameCurrencyInternalAccountId,
          destExternalAccountId: sameCurrencyExternalAccountId,
          amount: parsed,
        });
      } else {
        res = demoTransferIn({
          sourceExternalAccountId: sameCurrencyExternalAccountId,
          destInternalAccountId: sameCurrencyInternalAccountId,
          amount: parsed,
        });
      }

      if (res.success) {
        setResult(res.transaction);
        setStep("result");
        if (customerId) setInternalAccounts(getDemoInternalAccounts(customerId));
      } else {
        setError(res.error || "Transfer failed");
      }
    } catch (e: any) {
      setError(e.message || "Transfer failed");
    } finally {
      setLoading(false);
    }
  }, [amount, direction, sameCurrencyInternalAccountId, sameCurrencyExternalAccountId, customerId, demoTransferOut, demoTransferIn, getDemoInternalAccounts]);

  // ─── Live: Quote Handlers ─────────────────────────────────────

  const handleCreateQuote = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const parsedAmount = parseFloat(amount);
      if (isNaN(parsedAmount) || parsedAmount <= 0) {
        throw new Error("Please enter a valid positive amount.");
      }

      const minorAmount = amountToMinorUnits(parsedAmount, sourceCurrency);

      let destination: any;
      if (destType === "external-account") {
        destination = { destinationType: "ACCOUNT", accountId: destExternalAccountId };
      } else if (destType === "uma-address") {
        destination = {
          destinationType: "UMA_ADDRESS",
          umaAddress: destUmaAddress.trim(),
          currency: destUmaCurrency,
        };
      }

      const q = await createQuote({
        source: { sourceType: "REALTIME_FUNDING", customerId, currency: sourceCurrency },
        destination,
        lockedCurrencySide: lockedSide,
        lockedCurrencyAmount: minorAmount,
      });
      setQuote(q);
      setStep("review");
    } catch (e: any) {
      const detail = e.body ? JSON.stringify(e.body, null, 2) : "";
      const raw = (e.message || "Quote creation failed") + (detail && detail !== "{}" ? "\n" + detail : "");

      // Detect common backend config issues and show actionable guidance
      if (raw.includes("Platform currency not found")) {
        setError(
          "Platform currency not found\n\n" +
          "This usually means:\n" +
          "1. This customer was created before the backend currency/internal-account fix.\n" +
          "   → Try creating a NEW customer from the Customers page and retry.\n" +
          "2. The source currency (" + sourceCurrency + ") is not configured on your platform.\n" +
          "   → Check Settings > Platform Config for supported currencies.\n\n" +
          "Original error:\n" + detail
        );
      } else {
        setError(raw);
      }
    } finally {
      setLoading(false);
    }
  }, [amount, customerId, sourceCurrency, lockedSide, destType, destExternalAccountId, destUmaAddress, destUmaCurrency]);

  const handleExecuteQuote = useCallback(async () => {
    if (!quote) return;
    setLoading(true);
    setError("");
    try {
      const res = await executeQuote(quote.quoteId);
      setResult(res);
      setStep("result");
    } catch (e: any) {
      const detail = e.body ? JSON.stringify(e.body, null, 2) : "";
      setError((e.message || "Quote execution failed") + (detail && detail !== "{}" ? "\n" + detail : ""));
    } finally {
      setLoading(false);
    }
  }, [quote]);

  const handleSameCurrencyTransfer = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const parsedAmount = parseFloat(amount);
      if (isNaN(parsedAmount) || parsedAmount <= 0) {
        throw new Error("Please enter a valid positive amount.");
      }

      const ia = internalAccounts.find((a: any) => a.id === sameCurrencyInternalAccountId);
      const currency = ia?.balance?.currency?.code || ia?.currency || "USD";
      const minorAmount = amountToMinorUnits(parsedAmount, currency);

      let res;
      if (direction === "transfer-out") {
        res = await transferOut({
          source: { accountId: sameCurrencyInternalAccountId },
          destination: { accountId: sameCurrencyExternalAccountId },
          amount: minorAmount,
        });
      } else {
        res = await transferIn({
          source: { accountId: sameCurrencyExternalAccountId },
          destination: { accountId: sameCurrencyInternalAccountId },
          amount: minorAmount,
        });
      }
      setResult(res);
      setStep("result");
    } catch (e: any) {
      const detail = e.body ? JSON.stringify(e.body, null, 2) : "";
      setError((e.message || "Transfer failed") + (detail && detail !== "{}" ? "\n" + detail : ""));
    } finally {
      setLoading(false);
    }
  }, [amount, direction, sameCurrencyInternalAccountId, sameCurrencyExternalAccountId, internalAccounts]);

  const reset = () => {
    setStep("setup");
    setQuote(null);
    setResult(null);
    setError("");
    setAmount("");
    setDestExternalAccountId("");
    setDestUmaAddress("");
    setSameCurrencyInternalAccountId("");
    setSameCurrencyExternalAccountId("");
    setSourceAccountId("");
  };

  // ─── Computed values ─────────────────────────────────────────

  const isDemoQuoteValid =
    customerId &&
    sourceAccountId &&
    amount &&
    parseFloat(amount) > 0 &&
    ((destType === "external-account" && destExternalAccountId) ||
      (destType === "uma-address" && destUmaAddress.trim()));

  const isDemoSameCurrencyValid =
    customerId &&
    amount &&
    parseFloat(amount) > 0 &&
    sameCurrencyInternalAccountId &&
    sameCurrencyExternalAccountId;

  const isQuoteFormValid =
    customerId &&
    sourceCurrency &&
    amount &&
    parseFloat(amount) > 0 &&
    ((destType === "external-account" && destExternalAccountId) ||
      (destType === "uma-address" && destUmaAddress.trim()));

  const isSameCurrencyFormValid =
    customerId &&
    amount &&
    parseFloat(amount) > 0 &&
    sameCurrencyInternalAccountId &&
    sameCurrencyExternalAccountId;

  const hasInternalAccounts = internalAccounts.length > 0;

  // ─── Render helpers ──────────────────────────────────────────

  const renderAccountOption = (a: any) => {
    const currCode = a.balance?.currency?.code || a.currency?.code || a.currency || "?";
    const balanceStr = a.balance
      ? formatCurrencyAmount(a.balance)
      : a.accountInfo?.accountType || a.status || "N/A";
    const tag = a._label === "Platform" ? "Platform Treasury" : "Customer Wallet";
    const shortId = a.id.length > 30 ? a.id.slice(0, 30) + "..." : a.id;
    return `[${tag}] ${currCode}: ${balanceStr} (${shortId})`;
  };

  const renderExternalAccountOption = (a: any) => {
    const currCode = a.currency?.code || "?";
    const typeLabel = a.accountType?.replace(/_/g, " ") || "?";
    return `${typeLabel} - ${currCode} (${a.accountLabel || a.id})`;
  };

  // ─── Render ──────────────────────────────────────────────────

  return (
    <div className="p-8">
      <div className="mb-8">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold text-[#F8F8F7] tracking-tight">Transfer</h1>
          {demo.enabled && (
            <span className="text-[10px] font-bold uppercase tracking-wider bg-emerald-500/15 text-emerald-400 border border-emerald-500/20 px-2 py-0.5 rounded">
              Demo Mode
            </span>
          )}
        </div>
        <p className="text-sm text-[#8A8A8A] mt-1">
          {demo.enabled
            ? "Send payments via Quote API (cross-currency) or Transfer In/Out (same-currency)"
            : "Send payments using the Grid Quote API or same-currency transfers"}
        </p>
      </div>

      {error && (
        <pre className="mb-4 max-w-2xl bg-red-500/10 border border-red-500/30 text-red-400 px-4 py-3 rounded-lg text-sm whitespace-pre-wrap">
          {error}
        </pre>
      )}

      <div className="max-w-2xl">
        {/* ────────────────── SETUP STEP ────────────────── */}
        {step === "setup" && (
          <div className="space-y-6">
            {/* Customer Selection */}
            <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
              <h2 className="text-sm font-semibold text-[#F8F8F7] mb-3">Customer</h2>
              <select
                value={customerId}
                onChange={(e) => setCustomerId(e.target.value)}
                className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
              >
                <option value="">Select a customer...</option>
                {customers.map((c) => (
                  <option key={c.id} value={c.id}>
                    {customerDisplayName(c)} ({c.customerType || "INDIVIDUAL"})
                  </option>
                ))}
              </select>
            </div>

            {/* Warning if customer has no internal accounts (likely old/broken) */}
            {customerId && !demo.enabled && internalAccounts.length === 0 && (
              <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-4">
                <div className="text-sm text-yellow-400 font-medium mb-1">
                  No internal accounts found for this customer
                </div>
                <div className="text-xs text-yellow-400/70">
                  This customer may have been created before the backend fix. Quotes will likely fail with
                  "Platform currency not found". Try creating a <strong>new customer</strong> from the Customers page.
                </div>
              </div>
            )}

            {/* Destination Type Selector (same for demo and live) */}
            {customerId && (
              <>
                <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
                  <h2 className="text-sm font-semibold text-[#F8F8F7] mb-3">Transfer Type</h2>
                  <div className="grid grid-cols-3 gap-3">
                    <button
                      onClick={() => setDestType("external-account")}
                      className={`p-4 rounded-lg border text-left ${
                        destType === "external-account"
                          ? "border-[#00B3E0] bg-[#00B3E0]/5"
                          : "border-[#333333] hover:border-[#555555]"
                      }`}
                    >
                      <div className="text-sm font-medium text-[#F8F8F7]">Send Payment</div>
                      <div className="text-xs text-[#8A8A8A] mt-1">Quote: Internal &rarr; External</div>
                      <div className="text-[10px] text-[#666666] mt-1 font-mono">POST /quotes</div>
                    </button>
                    <button
                      onClick={() => setDestType("uma-address")}
                      className={`p-4 rounded-lg border text-left ${
                        destType === "uma-address"
                          ? "border-[#00B3E0] bg-[#00B3E0]/5"
                          : "border-[#333333] hover:border-[#555555]"
                      }`}
                    >
                      <div className="text-sm font-medium text-[#F8F8F7]">Send to UMA</div>
                      <div className="text-xs text-[#8A8A8A] mt-1">Quote: Internal &rarr; UMA</div>
                      <div className="text-[10px] text-[#666666] mt-1 font-mono">POST /quotes</div>
                    </button>
                    <button
                      onClick={() => setDestType("same-currency")}
                      className={`p-4 rounded-lg border text-left ${
                        destType === "same-currency"
                          ? "border-[#00B3E0] bg-[#00B3E0]/5"
                          : "border-[#333333] hover:border-[#555555]"
                      }`}
                    >
                      <div className="text-sm font-medium text-[#F8F8F7]">Same-Currency</div>
                      <div className="text-xs text-[#8A8A8A] mt-1">Transfer in/out</div>
                      <div className="text-[10px] text-[#666666] mt-1 font-mono">POST /transfer-*</div>
                    </button>
                  </div>
                </div>

                {/* ── Quote flow: External Account or UMA ── */}
                {(destType === "external-account" || destType === "uma-address") && (
                  <>
                    {/* Source Internal Account (demo) / Source Currency (live) */}
                    <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
                      {demo.enabled ? (
                        <>
                          <h2 className="text-sm font-semibold text-[#F8F8F7] mb-3">Source Internal Account</h2>
                          <p className="text-xs text-[#8A8A8A] mb-3">
                            Funds will be debited from this account. Maps to <span className="font-mono text-[#00B3E0]">source.sourceType: "ACCOUNT"</span>
                          </p>
                          <select
                            value={sourceAccountId}
                            onChange={(e) => setSourceAccountId(e.target.value)}
                            className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                          >
                            <option value="">Select source account...</option>
                            {internalAccounts.map((a: any) => (
                              <option key={a.id} value={a.id}>
                                {a.balance.currency.code}: {formatCurrencyAmount(a.balance)} ({a.id})
                              </option>
                            ))}
                          </select>
                        </>
                      ) : (
                        <>
                          <h2 className="text-sm font-semibold text-[#F8F8F7] mb-3">Source Currency</h2>
                          <p className="text-xs text-[#8A8A8A] mb-3">
                            The currency the customer is sending from.
                          </p>
                          <select
                            value={sourceCurrency}
                            onChange={(e) => setSourceCurrency(e.target.value)}
                            className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                          >
                            {supportedCurrencies.map((cur) => (
                              <option key={cur} value={cur}>
                                {cur}
                              </option>
                            ))}
                          </select>
                        </>
                      )}
                    </div>

                    {/* Destination: External Account */}
                    {destType === "external-account" && (
                      <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
                        <h2 className="text-sm font-semibold text-[#F8F8F7] mb-3">
                          Destination External Account
                        </h2>
                        <p className="text-xs text-[#8A8A8A] mb-3">
                          Maps to <span className="font-mono text-[#00B3E0]">destination.destinationType: "ACCOUNT"</span>
                        </p>
                        {externalAccounts.length === 0 ? (
                          <div className="text-sm text-[#8A8A8A]">
                            No external accounts found for this customer. Add one from the customer detail page.
                          </div>
                        ) : (
                          <select
                            value={destExternalAccountId}
                            onChange={(e) => setDestExternalAccountId(e.target.value)}
                            className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                          >
                            <option value="">Select an external account...</option>
                            {externalAccounts.map((a: any) => (
                              <option key={a.id} value={a.id}>
                                {demo.enabled ? renderExternalAccountOption(a) : renderAccountOption(a)}
                              </option>
                            ))}
                          </select>
                        )}
                      </div>
                    )}

                    {/* Destination: UMA Address */}
                    {destType === "uma-address" && (
                      <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
                        <h2 className="text-sm font-semibold text-[#F8F8F7] mb-3">UMA Address</h2>
                        <p className="text-xs text-[#8A8A8A] mb-3">
                          Maps to <span className="font-mono text-[#00B3E0]">destination.destinationType: "UMA_ADDRESS"</span>
                        </p>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs text-[#8A8A8A] mb-1">
                              Receiver UMA Address
                            </label>
                            <input
                              type="text"
                              value={destUmaAddress}
                              onChange={(e) => setDestUmaAddress(e.target.value)}
                              placeholder="$alice@domain.com"
                              className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm font-mono focus:border-[#00B3E0] focus:outline-none"
                            />
                          </div>
                          <div>
                            <label className="block text-xs text-[#8A8A8A] mb-1">
                              Destination Currency
                            </label>
                            <select
                              value={destUmaCurrency}
                              onChange={(e) => setDestUmaCurrency(e.target.value)}
                              className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                            >
                              {supportedCurrencies.map((cur) => (
                                <option key={cur} value={cur}>
                                  {cur}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Amount + Create Quote button */}
                    {((destType === "external-account" && destExternalAccountId) ||
                      (destType === "uma-address" && destUmaAddress.trim())) &&
                      (demo.enabled ? sourceAccountId : sourceCurrency) && (
                      <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
                        <h2 className="text-sm font-semibold text-[#F8F8F7] mb-3">Amount</h2>
                        <div className="space-y-3">
                          <input
                            type="number"
                            step="any"
                            min="0"
                            value={amount}
                            onChange={(e) => setAmount(e.target.value)}
                            placeholder="100.00"
                            className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm font-mono focus:border-[#00B3E0] focus:outline-none"
                          />
                          <select
                            value={lockedSide}
                            onChange={(e) => setLockedSide(e.target.value as "SENDING" | "RECEIVING")}
                            className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                          >
                            <option value="SENDING">Lock Sending Amount</option>
                            <option value="RECEIVING">Lock Receiving Amount</option>
                          </select>
                        </div>
                        <button
                          onClick={demo.enabled ? handleDemoCreateQuote : handleCreateQuote}
                          disabled={loading || (demo.enabled ? !isDemoQuoteValid : !isQuoteFormValid)}
                          className="mt-4 w-full bg-[#00B3E0] text-black font-semibold py-2.5 rounded-lg hover:bg-[#00C4F5] transition-colors disabled:opacity-50"
                        >
                          {loading ? "Creating Quote..." : "Create Quote"}
                        </button>
                      </div>
                    )}
                  </>
                )}

                {/* ── Same-Currency Transfer ── */}
                {destType === "same-currency" && (
                  <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
                    <h2 className="text-sm font-semibold text-[#F8F8F7] mb-3">
                      Same-Currency Transfer
                    </h2>
                    {!hasInternalAccounts ? (
                      <div className="bg-yellow-500/5 border border-yellow-500/20 rounded-lg p-4">
                        <div className="text-sm text-yellow-400 font-medium mb-1">
                          No internal accounts available
                        </div>
                        <div className="text-xs text-yellow-400/70">
                          {demo.enabled
                            ? "This customer has no internal accounts in demo data."
                            : "Enable Demo Mode to use mock internal accounts, or use the Quote-based flow."}
                        </div>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                          <button
                            onClick={() => setDirection("transfer-out")}
                            className={`p-3 rounded-lg border text-sm ${
                              direction === "transfer-out"
                                ? "border-[#00B3E0] text-[#00B3E0]"
                                : "border-[#333333] text-[#8A8A8A]"
                            }`}
                          >
                            <div>Transfer Out</div>
                            <div className="text-[10px] font-mono mt-0.5">POST /transfer-out</div>
                          </button>
                          <button
                            onClick={() => setDirection("transfer-in")}
                            className={`p-3 rounded-lg border text-sm ${
                              direction === "transfer-in"
                                ? "border-[#00B3E0] text-[#00B3E0]"
                                : "border-[#333333] text-[#8A8A8A]"
                            }`}
                          >
                            <div>Transfer In</div>
                            <div className="text-[10px] font-mono mt-0.5">POST /transfer-in</div>
                          </button>
                        </div>
                        <div>
                          <label className="block text-xs text-[#8A8A8A] mb-1">
                            Internal Account {direction === "transfer-out" ? "(source)" : "(destination)"}
                          </label>
                          <p className="text-[10px] text-[#555555] mb-1.5">
                            Customer Wallet = funds held for this customer. Platform Treasury = shared platform-level account.
                          </p>
                          <select
                            value={sameCurrencyInternalAccountId}
                            onChange={(e) => setSameCurrencyInternalAccountId(e.target.value)}
                            className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                          >
                            <option value="">Select internal account...</option>
                            {internalAccounts.map((a: any) => (
                              <option key={a.id} value={a.id}>
                                {renderAccountOption(a)}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs text-[#8A8A8A] mb-1">
                            External Account {direction === "transfer-out" ? "(destination)" : "(source)"}
                          </label>
                          {externalAccounts.length === 0 ? (
                            <div className="text-sm text-[#8A8A8A]">No external accounts found.</div>
                          ) : (
                            <select
                              value={sameCurrencyExternalAccountId}
                              onChange={(e) => setSameCurrencyExternalAccountId(e.target.value)}
                              className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm focus:border-[#00B3E0] focus:outline-none"
                            >
                              <option value="">Select external account...</option>
                              {externalAccounts.map((a: any) => (
                                <option key={a.id} value={a.id}>
                                  {demo.enabled ? renderExternalAccountOption(a) : renderAccountOption(a)}
                                </option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* Amount for Same-Currency */}
                {destType === "same-currency" &&
                  hasInternalAccounts &&
                  sameCurrencyInternalAccountId &&
                  sameCurrencyExternalAccountId && (
                    <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
                      <h2 className="text-sm font-semibold text-[#F8F8F7] mb-3">Amount</h2>
                      <input
                        type="number"
                        step="any"
                        min="0"
                        value={amount}
                        onChange={(e) => setAmount(e.target.value)}
                        placeholder="100.00"
                        className="w-full bg-[#0A0A0A] border border-[#333333] rounded-lg px-3 py-2.5 text-[#F8F8F7] text-sm font-mono focus:border-[#00B3E0] focus:outline-none"
                      />
                      <button
                        onClick={demo.enabled ? handleDemoSameCurrencyTransfer : handleSameCurrencyTransfer}
                        disabled={loading || (demo.enabled ? !isDemoSameCurrencyValid : !isSameCurrencyFormValid)}
                        className="mt-4 w-full bg-[#00B3E0] text-black font-semibold py-2.5 rounded-lg hover:bg-[#00C4F5] transition-colors disabled:opacity-50"
                      >
                        {loading
                          ? "Processing..."
                          : direction === "transfer-out"
                          ? "Execute Transfer Out"
                          : "Execute Transfer In"}
                      </button>
                    </div>
                  )}
              </>
            )}
          </div>
        )}

        {/* ────────────────── REVIEW QUOTE STEP ────────────────── */}
        {step === "review" && quote && (
          <div className="bg-[#111111] border border-[#222222] rounded-lg p-6">
            <h2 className="text-lg font-semibold text-[#F8F8F7] mb-4">Quote Details</h2>
            <div className="space-y-3 mb-6">
              <div className="grid grid-cols-2 gap-3">
                <div className="p-4 rounded-lg bg-[#0A0A0A] border border-[#222222]">
                  <div className="text-xs text-[#8A8A8A] mb-1">You Send</div>
                  <div className="text-lg font-mono font-semibold text-[#F8F8F7]">
                    {quote.totalSendingAmount != null
                      ? formatAmount(quote.totalSendingAmount, quote.sendingCurrency?.code || sourceCurrency)
                      : "N/A"}
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-[#0A0A0A] border border-[#222222]">
                  <div className="text-xs text-[#8A8A8A] mb-1">They Receive</div>
                  <div className="text-lg font-mono font-semibold text-emerald-400">
                    {quote.totalReceivingAmount != null
                      ? formatAmount(quote.totalReceivingAmount, quote.receivingCurrency?.code || "USD")
                      : "N/A"}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div className="p-3 rounded-lg bg-[#0A0A0A] border border-[#222222]">
                  <div className="text-xs text-[#8A8A8A] mb-1">Exchange Rate</div>
                  <div className="text-sm font-mono text-[#CCCCCC]">
                    {quote.exchangeRate != null ? quote.exchangeRate.toFixed(6) : "N/A"}
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-[#0A0A0A] border border-[#222222]">
                  <div className="text-xs text-[#8A8A8A] mb-1">Fees</div>
                  <div className="text-sm font-mono text-[#CCCCCC]">
                    {quote.feesIncluded != null
                      ? formatAmount(quote.feesIncluded, quote.sendingCurrency?.code || "USD")
                      : "N/A"}
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-[#0A0A0A] border border-[#222222]">
                  <div className="text-xs text-[#8A8A8A] mb-1">Expires</div>
                  <div className="text-sm text-[#CCCCCC]">
                    {quote.expiresAt ? new Date(quote.expiresAt).toLocaleTimeString() : "N/A"}
                  </div>
                </div>
              </div>
              <div className="p-3 rounded-lg bg-[#0A0A0A] border border-[#222222]">
                <div className="text-xs text-[#8A8A8A] mb-1">Quote ID</div>
                <div className="text-sm font-mono text-[#00B3E0] truncate">
                  {quote.quoteId || quote.id || "N/A"}
                </div>
              </div>
              <details className="rounded-lg bg-[#0A0A0A] border border-[#222222]">
                <summary className="p-3 text-xs text-[#8A8A8A] cursor-pointer hover:text-[#CCCCCC]">
                  View full quote response
                </summary>
                <pre className="px-3 pb-3 text-xs font-mono text-[#CCCCCC] overflow-auto max-h-48">
                  {JSON.stringify(quote, null, 2)}
                </pre>
              </details>
            </div>
            <div className="flex gap-3">
              <button
                onClick={reset}
                className="flex-1 border border-[#333333] text-[#F8F8F7] py-2.5 rounded-lg hover:bg-[#222222] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={demo.enabled ? handleDemoExecuteQuote : handleExecuteQuote}
                disabled={loading}
                className="flex-1 bg-[#00B3E0] text-black font-semibold py-2.5 rounded-lg hover:bg-[#00C4F5] transition-colors disabled:opacity-50"
              >
                {loading ? "Executing..." : "Execute Quote"}
              </button>
            </div>
          </div>
        )}

        {/* ────────────────── RESULT STEP ────────────────── */}
        {step === "result" && (
          <div className="bg-[#111111] border border-[#222222] rounded-lg p-6 text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/10 flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-emerald-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                strokeWidth={2}
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
              </svg>
            </div>
            <h2 className="text-xl font-semibold text-[#F8F8F7] mb-2">Transfer Complete</h2>

            {result && (
              <div className="mt-4 text-left">
                {(result.sentAmount || result.receivedAmount || result.amount) && (
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="p-4 rounded-lg bg-[#0A0A0A] border border-[#222222]">
                      <div className="text-xs text-[#8A8A8A] mb-1">Sent</div>
                      <div className="text-lg font-mono font-semibold text-[#F8F8F7]">
                        {safeFormatAmount(result.sentAmount || result.amount)}
                      </div>
                    </div>
                    <div className="p-4 rounded-lg bg-[#0A0A0A] border border-[#222222]">
                      <div className="text-xs text-[#8A8A8A] mb-1">Received</div>
                      <div className="text-lg font-mono font-semibold text-emerald-400">
                        {safeFormatAmount(result.receivedAmount || result.amount)}
                      </div>
                    </div>
                  </div>
                )}
                <pre className="text-xs font-mono text-[#CCCCCC] bg-[#0A0A0A] p-4 rounded-lg overflow-auto max-h-64">
                  {JSON.stringify(result, null, 2)}
                </pre>
              </div>
            )}

            <button
              onClick={reset}
              className="mt-6 bg-[#00B3E0] text-black font-semibold px-6 py-2.5 rounded-lg hover:bg-[#00C4F5] transition-colors"
            >
              New Transfer
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
